import React, { useState, useEffect, useCallback } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import { DevicesAPI } from '../services/portalApi';
import { PortalConfirmModal } from '../components/PortalConfirmModal';

interface Device {
  id: string;
  device_name: string;
  credential_id: string;
  registered_at: string;
  last_used_at: string | null;
  is_active: boolean;
}

export const PortalDevices: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [confirmTarget, setConfirmTarget] = useState<{ id: string; name: string } | null>(null);

  const fetchDevices = useCallback(async () => {
    try {
      const { data } = await DevicesAPI.list();
      setDevices(data);
    } catch {
      setError('Failed to load devices');
    }
  }, []);

  useEffect(() => { fetchDevices(); }, [fetchDevices]);

  const handleRegister = async () => {
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const { data: options } = await DevicesAPI.registerStart();
      const credential = await startRegistration({ optionsJSON: options });
      await DevicesAPI.registerFinish({
        credential,
        device_name: deviceName || `${navigator.platform || 'Device'} - ${new Date().toLocaleDateString()}`,
      });
      setSuccess('Device registered successfully');
      setDeviceName('');
      await fetchDevices();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = (id: string, name: string) => {
    setConfirmTarget({ id, name });
  };

  const confirmDeactivate = async () => {
    if (!confirmTarget) return;
    setConfirmTarget(null);
    setError('');
    setSuccess('');
    try {
      await DevicesAPI.deactivate(confirmTarget.id);
      setSuccess(`Device "${confirmTarget.name}" deactivated`);
      await fetchDevices();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to deactivate device');
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: 24 }}>Passkey Devices</h2>

      {error && <div className="portal-alert portal-alert-error">{error}</div>}
      {success && <div className="portal-alert portal-alert-success">{success}</div>}

      {/* Register New Device */}
      <div className="portal-card">
        <h3>Register New Device</h3>
        <div className="portal-flex portal-gap-8" style={{ alignItems: 'flex-end' }}>
          <div className="portal-form-group" style={{ flex: 1, marginBottom: 0 }}>
            <label>Device Name (optional)</label>
            <input
              className="portal-input"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="e.g. Work Laptop"
            />
          </div>
          <button
            className="portal-btn portal-btn-primary"
            onClick={handleRegister}
            disabled={loading}
            style={{ marginBottom: 0 }}
          >
            {loading ? 'Registering...' : 'Register Passkey'}
          </button>
        </div>
      </div>

      {/* Device List */}
      <div className="portal-card">
        <h3>Registered Devices ({devices.length})</h3>
        {devices.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>No devices registered.</p>
        ) : (
          <div className="portal-table-wrap">
            <table className="portal-table">
              <thead>
                <tr>
                  <th>Device Name</th>
                  <th>Registered</th>
                  <th>Last Used</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.id}>
                    <td style={{ fontWeight: 500 }}>{d.device_name}</td>
                    <td>{new Date(d.registered_at).toLocaleDateString()}</td>
                    <td>{d.last_used_at ? new Date(d.last_used_at).toLocaleString() : 'Never'}</td>
                    <td>
                      <span className={`portal-badge ${d.is_active ? 'portal-badge-green' : 'portal-badge-red'}`}>
                        {d.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {d.is_active && devices.length > 1 && (
                        <button
                          className="portal-btn portal-btn-danger portal-btn-sm"
                          onClick={() => handleDeactivate(d.id, d.device_name)}
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PortalConfirmModal
        open={!!confirmTarget}
        title="Deactivate Device"
        message={`Deactivate device "${confirmTarget?.name}"? You won't be able to use it to log in.`}
        confirmLabel="Deactivate"
        danger
        onConfirm={confirmDeactivate}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  );
};
